<template>
  <div :class="['category-selector', {'has-scroll': hasScroll}]">
    <div class="category-selector-header">
      <button class="close-btn" @click="$emit('close')">
        <i class="icon icon--close-size16"></i>
      </button>
    </div>
    <div ref="scrollArea" class="category-selector-body">
      <div class="category-wrapper">
        <template v-for="([categoryKey, domainCategory]) in aiCategoryList">
          <div v-if="categoryKey && domainCategory" class="classification" :key="categoryKey">
            <p :class="['classification-header', { selected: selected in domainCategory.subcategories }]">
              <i :class="['icon', DOMAIN_CATEGORY_ICON[categoryKey]]"></i>
              <span>{{ domainCategory.title[$i18n.locale] }}</span>
            </p>
            <ul class="category-list">
              <li
                class="category-list-item"
                v-for="([subCategoryKey, subCategory]) in domainCategory.subcategories"
                :key="subCategoryKey"
                :class="[{ selected: selected === subCategoryKey }, { disabled: !aiCategoryCount[subCategoryKey]|| aiCategoryCount[subCategoryKey] <= 0 }]"
                @click="$emit('select', subCategoryKey)"
              >
                <i class="icon matched icon--star-green" v-if="matched.includes(subCategoryKey)"></i>
                <span class="text">{{ subCategory.title[$i18n.locale].replaceAll('/', ' / ') }}</span>
                <span
                  class="invalid-tooltip"
                  v-if="!aiCategoryCount[subCategoryKey] || aiCategoryCount[subCategoryKey] <= 0"
                  v-btf-tooltip:bottom.start="{contents: $t( `benchmark.noDataNotice`), offsetY: -10, size: 266}"
                  @click.stop
                >
                </span>
              </li>
            </ul>
          </div>
        </template>
      </div>
    </div>
    <div class="category-selector-footer">
      <div class="legend">
        <div class="legend-item"><div class="icon-matched"></div><span>{{ $t( `benchmark.selectedLabel`) }}</span></div>
        <div class="legend-item"><i class="icon icon--star-green"></i><span>{{ $t( `benchmark.matchedLabel`) }}</span></div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getAiCategoryCount } from '@/api/ai'
import { DOMAIN_CATEGORY_ICON } from '@/libs/constants'

export default {
  name: 'CategorySelector',
  components: {},
  props: {
    selected: {
      type: String,
      required: true
    },
    matched: {
      type: Array,
      required: true
    },
    categoryList: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      DOMAIN_CATEGORY_ICON,
      aiCategoryCount: {},
      hasScroll: false
    }
  },
  computed: {
    ...mapState([
      'planData'
    ]),
    aiCategoryList () {
      if (!this.categoryList) return null

      const { cache, ...categories } = this.categoryList
      return Object.entries(categories)
        .sort(([, categoryA], [, categoryB]) =>
          categoryA.order - categoryB.order
        )
        .map(([key, category]) => [
          key,
          {
            ...category,
            subcategories: Object.entries(category.subcategories)
              .sort(([, categoryA], [, categoryB]) =>
                categoryA.title[this.$i18n.locale].localeCompare(categoryB.title[this.$i18n.locale])
              )
          }
        ])
    }
  },
  mounted () {
    getAiCategoryCount()
      .then(res => {
        this.aiCategoryCount = res
      })
      .catch(e => console.log(e))
    this.checkScroll()
    window.addEventListener('resize', this.checkScroll)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.checkScroll)
  },
  methods: {
    checkScroll () {
      this.$nextTick(() => {
        const scrollArea = this.$refs.scrollArea
        if (scrollArea) {
          this.hasScroll = scrollArea.scrollHeight > scrollArea.clientHeight
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.category-selector {
  display: flex;
  flex-direction: column;
  height: 690px;
  max-height: calc(100% - 144px);
  min-height: 480px;
  width: 968px;
  border-radius: 14px;
  background-color: #fff;
  font-weight: 400;
  color: #2f2f2f;
  box-sizing: border-box;
  &-header {
    height: 30px;
    position: relative;
    flex-shrink: 0;
    .close-btn {
      position: absolute;
      top: 12px;
      right: 12px;
    }
  }
  &-body {
    display: flex;
    flex-direction: column;
    padding: 12px 22px 19px 28px;
    overflow-y: auto;

    .notification {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 26px;
      color: #444;
      font-size: 13px;
      font-weight: 500;
      line-height: 120%;
    }

    .category-wrapper {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      row-gap: 31px;
      column-gap: 8px;

    .classification {
      &-header {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 15px;
        font-weight: 600;
        color: #2F2F2F;
        margin-bottom: 8px;
      }
      .category-list {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding-left: 18px;
        &-item {
          position: relative;
          padding: 4px 6px;
          border-radius: 4px;
          color: #777;
          font-size: 13px;
          font-weight: 500;
          line-height: 120%;
          cursor: pointer;
          .text {
            word-break: break-word;
          }

          &:hover {
            background-color: #EBEBEB;
          }

          &.selected {
            background-color: #EC0047;
            color: #fff;
          }

          &.disabled {
            pointer-events: none;
            background-color: transparent;
            cursor: default;
            .text {
              opacity: 0.3;
            }
            .invalid-tooltip {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              pointer-events: all;
            }
          }

          .icon.matched {
            position: absolute;
            top: 50%;
            left: -3px;
            transform: translate(-100%, -50%);

          }
        }
      }
    }
    }
  }
  &-footer {
    padding: 24px 28px;
    display: flex;
    justify-content: flex-end;
    .legend {
      display: flex;
      align-items: center;
      gap: 10px;
      &-item {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #666;
        font-size: 12px;
        font-weight: 500;
        .icon-matched {
          width: 12px;
          height: 12px;
          flex-shrink: 0;
          border-radius: 2px;
          background-color: #EC0047;
        }
      }
    }
  }

  &.has-scroll {
    .category-selector-header {
      &:after {
        content: '';
        position: absolute;
        width: 100%;
        height: 12px;
        left: 0;
        bottom: 0;
        z-index: 100;
        background: linear-gradient(0deg, rgba(255, 255, 255, 0.00) 0%, #FFF 100%);
        transform: translateY(100%);
      }
    }
    .category-selector-footer {
      position: relative;
      &:before {
        content: '';
        position: absolute;
        width: 100%;
        height: 12px;
        left: 0;
        bottom: 100%;
        z-index: 100;
        background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.06) 100%);
      }
    }
  }
}
</style>
